import { Box, Skeleton, Stack, Typography } from '@mui/material';
import _ from 'lodash';
import moment from 'moment';
import { MUIDataTableColumnDef } from 'mui-datatables';
import React from 'react';
import { setLinearLoading } from '../../../component/linear-determinate';
import GlobalNotification from '../../../component/common/snackbar';
import Table from '../../../component/table';
import i18n from '../../../i18n';
import { apiManage_rbp as apiManage } from '../../../request';
import '../scss/print-queue.styles.scss';

/** 首屏队列数据加载中 */
function PrintQueuePageSkeleton() {
    const rowKeys = [0, 1, 2, 3, 4, 5, 6, 7];
    return (
        <Box className="print-queue-page print-queue-page--shell">
            <Box className="print-queue-page__card">
                <Stack spacing={2} sx={{ width: '100%' }}>
                    <Skeleton variant="text" width={220} height={32} sx={{ borderRadius: 1 }} />
                    <Skeleton variant="rounded" height={44} sx={{ borderRadius: 1, width: '100%' }} />
                    {rowKeys.map((k) => (
                        <Skeleton key={`print-queue-sk-${k}`} variant="rounded" height={48} sx={{ borderRadius: 1, width: '100%' }} />
                    ))}
                </Stack>
            </Box>
        </Box>
    );
}

export default class PrintQueue extends React.Component<Prop, State> {
    public state: Readonly<State> = {
        data: [],
        rowsPerPage: 20,
        currPage: 0,
        initialLoading: true,
    };

    async componentDidMount() {
        await this.init();
    }

    /**
     * 初始化
     */
    async init() {
        setLinearLoading(true);
        try {
            const result = await apiManage.getPrintQueue();
            if (!_.get(result, 'error')) {
                result.map((n: any, index: number) => {
                    let iData: any;
                    try {
                        iData = n.data;
                        n.order_head_id = _.get(iData, '[0].order_head_id', '');
                        n.print_device_name = this.getTablePrinter(result, index);
                        n.table = this.getTableName(result, index);
                    } catch {
                        console.log('n.data', index, n.data);
                        n.data = '[]';
                        n.order_head_id = undefined;
                    }
                    return n;
                });
                this.setState({
                    data: result,
                });
            }
        } finally {
            setLinearLoading(false);
            this.setState({ initialLoading: false });
        }
        return {};
    }

    /**
     * 处理桌台名称
     */
    public getTableName(data: any[], dataIndex: number) {
        const newData = data[dataIndex].data;
        if (data[dataIndex].version === 'JK') {
            return _.get(newData, '[0].table_name', '');
        }
        return _.isArray(newData)
            ? newData
                .map((item: any) => {
                    return Object.keys(item.print_devices).map((key) => {
                        return _.get(item.print_devices[key].data, 'table', '');
                    });
                })
                .flat()[0]
            : '';
    }

    /**
     * 处理打印机
     */
    public getTablePrinter(data: any[], dataIndex: number) {
        const newData = data[dataIndex].data;
        if (data[dataIndex].version === 'JK' || data[dataIndex].print_type === 'qrcode') {
            return _.get(newData, '[0].print_device_name', '');
        }
        return _.isArray(newData)
            ? newData
                .map((item: any) => {
                    return Object.keys(item.print_devices).map((key) => {
                        return _.get(item.print_devices[key], 'data.print_device_name', '') || '';
                    });
                })
                .flat()
                .join(',')
            : newData.printer.map((item: any) => item.print_device_name).flat().join(',');
    }

    /**
     * 调 api 把当前 id 传过去（重新打印）
     */
    async postQueue(id: number) {
        const res = await apiManage.postPrintQueue(id, {
            class: { id },
        });
        if (!res.error) {
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            await this.init();
        } else {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        }
    }

    /**
     * 清空打印队列
     */
    public clearPrintQueue = async () => {
        const res = await apiManage.postClearPrintQueue();
        if (!res.error) {
            GlobalNotification.success(i18n.t('print_queue_page_clear'));
            this.init();
        } else {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        }
    };

    render() {
        if (this.state.initialLoading) {
            return <PrintQueuePageSkeleton />;
        }
        const { data, rowsPerPage } = this.state;

        const columns: MUIDataTableColumnDef[] = [
            {
                name: 'id',
                label: i18n.t('print_queue_page_column_id'),
                options: {
                    sort: true,
                    filter: false,
                },
            },
            {
                name: 'print_type',
                label: i18n.t('print_queue_page_print_type'),
                options: {
                    sort: true,
                },
            },
            {
                name: 'order_head_id',
                label: i18n.t('print_queue_page_order_number'),
                options: {
                    sort: true,
                    sortCompare: (order) => {
                        return (obj1, obj2) => {
                            const val1 = Number(obj1.data);
                            const val2 = Number(obj2.data);
                            return (val1 - val2) * (order === 'asc' ? 1 : -1);
                        };
                    },
                },
            },
            {
                name: 'table',
                label: i18n.t('dinner_table_page_table_name'),
                options: {
                    sort: false,
                },
            },
            {
                name: 'date',
                label: i18n.t('print_queue_page_send_printer_time'),
                options: {
                    sort: true,
                    filter: false,
                    customBodyRenderLite: (dataIndex: number) => {
                        const raw = _.get(data, [dataIndex, 'date']);
                        const m = moment(raw, 'DD/MM/YYYY HH:mm', true);
                        if (!m.isValid()) {
                            return raw != null ? String(raw) : '';
                        }
                        return (
                            <>
                                {m.format('DD-MM-YYYY')}
                                <br />
                                {m.format('HH:mm')}
                            </>
                        );
                    },
                },
            },
            {
                name: 'print_device_name',
                label: i18n.t('print_queue_page_printer_address'),
                options: {
                    sort: false,
                },
            },
            // {
            //     name: '__reprint',
            //     label: i18n.t('print_queue_page_reprint'),
            //     options: {
            //         filter: false,
            //         sort: false,
            //         download: false,
            //         searchable: false,
            //         customBodyRenderLite: (dataIndex: number) => {
            //             const rowId = _.get(data, [dataIndex, 'id']) as number | undefined;
            //             return (
            //                 <IconButton
            //                     type="button"
            //                     className="print-queue-page__reprint"
            //                     aria-label={i18n.t('print_queue_page_reprint')}
            //                     disabled={rowId == null}
            //                     onClick={() => {
            //                         if (rowId != null) {
            //                             this.postQueue(rowId);
            //                         }
            //                     }}
            //                     size="large"
            //                 >
            //                     <PrintRounded sx={{ fontSize: 40 }} />
            //                 </IconButton>
            //             );
            //         },
            //     },
            // },
        ];

        const toolbarTitle = (
            <Stack spacing={2} alignItems="flex-start">
                <Typography className="print-queue-page__toolbar-title" component="div">
                    {i18n.t('print_queue_page_label')}
                </Typography>
            </Stack>
        );

        return (
            <Box className="print-queue-page print-queue-page--shell">
                <Box className="print-queue-page__card">
                    <Table
                        title={toolbarTitle}
                        data={data}
                        columns={columns}
                        elevation={0}
                        otherOption={{
                            search: true,
                            pagination: true,
                            rowsPerPage,
                            filter: true,
                            filterType: 'multiselect',
                        }}
                        centerCellContent
                    />
                </Box>
            </Box>
        );
    }
}

type Prop = {};

type State = {
    data: any[];
    rowsPerPage: number;
    currPage: number;
    /** 首次 init 完成前展示骨架屏（再次 init 如重打、清空仍为 false） */
    initialLoading: boolean;
};
